import { IconButton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toSpanishDate } from "../../../helpers/dateFormat";
import {
  numberFormat,
  participationsFormat,
} from "../../../helpers/numberFormat";
import { TableLayout } from "../../layout/TableLayout";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { deleteInvestment } from "../../helpers/investments";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { EditInvestment } from "../investments/EditInvestment";
import { selectInvestment } from "../../../store/investment/investmentSlice";
import { CreateDivestment } from "../divestment/CreateDivestment";

export const PositionViewTable = ({
  rows,
  name,
  setReloadData,
  vehiclesList,
}) => {
  const { t } = useTranslation("global");
  const dispatch = useDispatch();
  const { userToAction } = useSelector((state) => state.partner);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDivestment, setOpenDivestment] = useState(false);

  const onDivestmentClick = (e) => {
    dispatch(selectInvestment(e.row));
    setOpenDivestment(true);
  };

  const onEditClick = (e) => {
    dispatch(selectInvestment(e.row));
    setOpenEdit(true);
  };

  const onDeleteClick = async (e) => {
    await deleteInvestment(
      e,
      t("delete.successMsg"),
      t("delete.errorMsg"),
      t("delete.cancelButton"),
      t("delete.confirmButton"),
      t("delete.deleteConfirm")
    );
    setReloadData((prevState) => !prevState);
  };

  const columns = [
    {
      field: "startup",
      headerName: t("positions.startup"),
      minWidth: 200,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "investmentDate",
      headerName: t("positions.investmentDate"),
      minWidth: 140,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography color="primary" variant="body2">
          {toSpanishDate(params.row.investmentDate)}
        </Typography>
      ),
    },
    {
      field: "roundNumber",
      headerName: t("positions.roundNumber"),
      minWidth: 90,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "whoInvests",
      headerName: t("positions.whoInvests"),
      minWidth: 200,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "idCrm",
      headerName: t("partnerlist.idCrm"),
      minWidth: 90,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "investmentType",
      headerName: t("positions.investmentType"),
      minWidth: 180,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <Typography color="primary" variant="body2">
            {row?.investmentType === "Ampliación Capital"
              ? t("opportunities.investmentTypeCapitalIncrease")
              : t("opportunities.investmentTypeEquityLoan")}
          </Typography>
        );
      },
    },
    {
      field: "amount",
      headerName: t("positions.amount"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => numberFormat(params.row.amount),
    },
    {
      field: "participationValuation",
      headerName: t("positions.participationValuation"),
      minWidth: 160,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => {
        const resp = params.row.participationValuation;
        return isNaN(resp) || resp === null || resp === "Infinity"
          ? "-"
          : numberFormat(resp);
      },
    },
    {
      field: "shares",
      headerName: t("positions.shares"),
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => {
        const resp = (
          params.row.amount / params.row.participationValuation
        ).toFixed(2);
        return isNaN(resp) || resp === null || resp === "Infinity"
          ? "-"
          : participationsFormat(resp);
      },
    },
    {
      field: "newAmount",
      headerName: t("positions.newAmount"),
      minWidth: 140,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => {
        const resp = numberFormat(params?.row?.newAmount);
        return resp === "NaN" ? "-" : resp;
      },
    },
    {
      field: "capitalGain",
      headerName: t("positions.capitalGain"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        const sharesAmount = row.amount / row.participationValuation;
        const newCapital = sharesAmount * row.newAmount;
        const resp = (newCapital - row.amount).toFixed(2);
        return isNaN(resp) ? "-" : numberFormat(resp);
      },
    },
    {
      field: "dividends",
      headerName: t("positions.dividends"),
      minWidth: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        const dividendsSum = (row.dividends || [])
          .map((div) => div.amount)
          .reduce((a, b) => a + b, 0);
        return numberFormat(dividendsSum);
      },
    },
    {
      field: "url",
      headerName: t("positions.url"),
      minWidth: 160,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <Stack direction="row" gap={0}>
            <a href={params.row.url} target="_blank" rel="noreferrer">
              {t("link.link")}
            </a>
          </Stack>
        );
      },
    },
    {
      field: "action",
      headerName: t("partnerlist.columnActions"),
      maxWidth: 130,
      align: "center",
      headerAlign: "center",
      flex: 0,
      sortable: false,
      renderCell: (params) => {
        return (
          <Stack direction="row" gap={0}>
            <IconButton
              color="secondary"
              onClick={() => onDivestmentClick(params)}
            >
              <AttachMoneyIcon />
            </IconButton>

            <IconButton color="secondary" onClick={() => onEditClick(params)}>
              <EditRoundedIcon />
            </IconButton>

            <IconButton color="secondary" onClick={() => onDeleteClick(params)}>
              <DeleteOutlineRoundedIcon />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  return (
    <>
      <TableLayout
        rows={rows}
        columns={columns}
        title="positionsViewList"
        name={name}
      />
      {userToAction && openEdit && (
        <EditInvestment
          setHidden={setOpenEdit}
          setReloadData={setReloadData}
          vehiclesList={vehiclesList}
        />
      )}
      {userToAction && openDivestment && (
        <CreateDivestment
          setHidden={setOpenDivestment}
          setReloadData={setReloadData}
        />
      )}
    </>
  );
};
